import { FILTERBUTTONCLICKED } from "../../utils/bus-events"
import bus from "../../utils/bus"

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

type filtersProps = {
  filters: string[]
  entity?: string
  pagination?: boolean
}

export default function filters(el: HTMLElement, props: filtersProps) {
  const urlParams = new URLSearchParams(window.location.search)
  const url = new URL(window.location.href)
  let currentFilters: Record<string, string> = {}
  const entity = props.entity

  const cardsNodes = el.querySelectorAll(".card")

  props.filters.forEach(filter => {
    selectFilter(filter, el, currentFilters, urlParams)
  })

  function selectFilter(
    filter: string,
    el: HTMLElement,
    currentFilters: Record<string, string>,
    urlParams: URLSearchParams,
  ) {
    let urlFilterValue = urlParams.get(filter)
    currentFilters[filter] = urlFilterValue ?? "all"

    const filterBtn: HTMLElement | null = el.querySelector(
      `.${filter}-filter__button`,
    )
    const filterSvg = filterBtn?.querySelector("svg")
    const filterDropdown: HTMLElement | null = el.querySelector(
      `.${filter}-filter__dropdown`,
    )
    const selectButtons: NodeListOf<HTMLElement> = el.querySelectorAll(
      `.${filter}-filter__dropdown-button`,
    )
    let filterBtnTitle: HTMLElement | null = el.querySelector(
      `.${filter}-filter__button-title`,
    )

    if (
      filterBtnTitle === null ||
      selectButtons === null ||
      filterBtn == null
    ) {
      return
    }
    if (currentFilters[filter] !== "all") {
      filterBtn.classList.add("dropdown-button--active")
    }
    filterBtn.addEventListener("click", e => {
      e.preventDefault()
      if (filterSvg && filterDropdown) {
        if (!filterDropdown.classList.contains("open")) {
          openDropdown(filterDropdown, filterBtn)
        } else {
          closeDropdown(filterDropdown, filterBtn)
        }
      }
    })

    selectButtons.forEach(button => {
      let selectedBtn = button as HTMLElement
      const isResetFilter = selectedBtn.dataset[filter] == "all"
      const filterLabel = isResetFilter
        ? filterBtnTitle.dataset.default
        : undefined

      if (filterBtnTitle) {
        checkURLsearchParams(button, selectButtons, filterBtnTitle)
      }
      button.addEventListener("click", e => {
        e.preventDefault()
        bus.emit(FILTERBUTTONCLICKED)
        url.searchParams.delete("page")
        if (filterBtnTitle) {
          updateSelectedButton(
            selectButtons,
            filterBtnTitle,
            isResetFilter ? undefined : selectedBtn,
            filterLabel,
          )
        }
        for (const property in currentFilters) {
          const filterValue = selectedBtn.dataset[property]
          if (filterValue) {
            currentFilters[filter] = filterValue
            applyFilters(currentFilters)
          }
        }
        if (props.pagination) {
          window.location.reload()
        }
      })
    })

    if (filterSvg && filterBtn && filterDropdown) {
      closeDropdownClickingOutside(filterBtn, filterDropdown)
    }
  }

  // Applies the current filters to the card elements and updates the URL parameters.
  function applyFilters(currentFilters: Record<string, string>) {
    updateURLParams()
    cardsNodes.forEach(node => {
      let cardEl = node as HTMLElement
      showCard(cardEl)
      for (const property in currentFilters) {
        const cardFilterValues = cardEl.dataset[property]
        if (cardFilterValues) {
          if (
            currentFilters[property] !== "all" &&
            !cardFilterValues.includes(currentFilters[property])
          ) {
            hideCard(cardEl)
          }
        }
      }
    })
    displaySearchResult()
  }

  // Updates the URL parameters based on the current filters.
  function updateURLParams() {
    for (const property in currentFilters) {
      if (currentFilters[property] === "all") {
        url.searchParams.delete(property)
      } else {
        url.searchParams.set(property, currentFilters[property])
      }
    }
    window.history.pushState({}, "", url.toString())
  }

  function showCard(cardNode: HTMLElement) {
    cardNode.setAttribute("data-show", "true")
    cardNode.classList.remove("hidden")
  }

  function hideCard(cardNode: HTMLElement) {
    cardNode.setAttribute("data-show", "false")
    cardNode.classList.add("hidden")
  }

  function openDropdown(filterDropdown: HTMLElement, filterBtn: HTMLElement) {
    filterBtn.classList.add("dropdown-button--active", "open")
    filterDropdown.classList.add("open", "dropdown-menu-container--active")
    filterBtn.setAttribute("aria-expanded", "true")
  }

  function closeDropdown(filterDropdown: HTMLElement, filterBtn: HTMLElement) {
    const isActiveFilter = Object.keys(currentFilters)
      .filter(key => currentFilters[key] !== "all")
      .some(key => filterBtn.classList.contains(`${key}-filter__button`))
    if (!isActiveFilter) filterBtn.classList.remove("dropdown-button--active")
    filterBtn.classList.remove("open")
    filterDropdown.classList.remove("open", "dropdown-menu-container--active")
    filterBtn.setAttribute("aria-expanded", "false")
  }

  function closeDropdownClickingOutside(
    filterDropdown: HTMLElement,
    filterBtn: HTMLElement,
  ) {
    document.addEventListener("click", event => {
      const targetEvent = event.target as HTMLElement
      if (!filterDropdown.contains(targetEvent)) {
        closeDropdown(filterBtn, filterDropdown)
      }
    })
  }

  function updateSelectedButton(
    allSelectedBtn: NodeListOf<HTMLElement>,
    filterBtnTitle: HTMLElement,
    nextSelectedBtn?: HTMLElement,
    label?: string,
  ) {
    allSelectedBtn.forEach(button => {
      button.setAttribute("aria-selected", "false")
    })
    if (nextSelectedBtn) {
      nextSelectedBtn.setAttribute("aria-selected", "true")
      filterBtnTitle.innerHTML = nextSelectedBtn.innerHTML
    }
    if (label) {
      filterBtnTitle.innerHTML = label
    }
  }

  function displaySearchResult() {
    const searchResultEl: HTMLSpanElement | null = el.querySelector(
      ".filter-search__result",
    )
    const searchResultWrapper: HTMLElement | null = el.querySelector(
      ".filter-search__result-wrapper",
    )
    const searchResult: NodeListOf<HTMLElement> =
      el.querySelectorAll("[data-show='true']")

    let activeFilters = []
    for (const property in currentFilters) {
      currentFilters[property] === "all"
        ? activeFilters.push("")
        : activeFilters.push(currentFilters[property])
    }
    activeFilters = activeFilters.filter(item => item)
    if (searchResultEl && searchResultWrapper) {
      const clearAllFiltersBtn: HTMLElement | null =
        el.querySelector(".clear-filter")

      if (cardsNodes.length === 0) {
        searchResultWrapper.classList.remove("hidden")
        searchResultEl.classList.remove("hidden")
      } else if (activeFilters.length === 0) {
        searchResultWrapper.classList.add("hidden")
      } else if (activeFilters.length > 0 && searchResult.length === 0) {
        searchResultWrapper.classList.remove("hidden")
        searchResultEl.classList.remove("hidden")
      } else {
        searchResultEl.classList.add("hidden")
        searchResultWrapper.classList.remove("hidden")
      }

      clearAllFiltersBtn?.addEventListener("click", e => {
        searchResultWrapper.classList.add("hidden")
        clearAllFilters()
      })
    }
  }

  function clearAllFilters() {
    props.filters.forEach(filter => {
      const allSelectButtons: NodeListOf<HTMLElement> = el.querySelectorAll(
        `.${filter}-filter__dropdown-button`,
      )

      const allFilterBtnTitle = el.querySelectorAll(
        `.${filter}-filter__button-title`,
      ) as NodeListOf<HTMLElement>

      const allFilterBtns = el.querySelectorAll(
        `.${filter}-filter__button`,
      ) as NodeListOf<HTMLElement>

      currentFilters[filter] = "all"

      allSelectButtons.forEach(selectButton => {
        selectButton.setAttribute("aria-selected", "false")
      })
      allFilterBtns.forEach(filterBtn => {
        filterBtn.classList.remove("dropdown-button--active")
      })
      allFilterBtnTitle.forEach(buttonTitle => {
        buttonTitle.innerHTML = capitalizeFirstLetter(
          filter.split("_").join(" "),
        )
      })
    })
    applyFilters(currentFilters)
    if (props.pagination) {
      window.location.reload()
    }
  }

  function checkURLsearchParams(
    button: HTMLElement,
    selectButtons: NodeListOf<HTMLElement>,
    filterBtnTitle: HTMLElement,
  ) {
    let shouldApplyFilters = false
    for (const property in currentFilters) {
      if (filterBtnTitle === null) return
      if (
        button.dataset[property] !== "all" &&
        button.dataset[property] === currentFilters[property]
      ) {
        shouldApplyFilters = true
        let nextSelectedbtn = button as HTMLElement
        updateSelectedButton(selectButtons, filterBtnTitle, nextSelectedbtn)
      }
    }
    if (shouldApplyFilters) applyFilters(currentFilters)
  }

  applyFilters(currentFilters)
}
